import { useEffect, useState } from 'react';
import { AutocompleteDropdown } from 'src/shared/components/form/autocompleteDropdown';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { getAllFundraiser } from 'src/shared/services/fundraiserService';
import { getNestedFormErrors } from 'src/shared/functions/getNestedFormErrors';
import { useFormContext, useFormState } from 'react-hook-form';
import ControlledTextField from 'src/shared/components/form/controlledTextField';
import { Box, FormHelperText } from '@mui/material';
import { FundraiserSearchFilter } from 'src/modules/fundraiser/models/fundraiserSearchFilter';
import { FundraiserResponse } from '../../fundraiser/models/fundraiserResponse';

type FundraiserPickerProps = {
  name: string;
  label: string;
  initialValue: number | undefined;
  callback?: (fundraiserId: number | undefined) => void;
};

export const FundraiserPicker = (props: FundraiserPickerProps) => {
  const { name, label, initialValue, callback = undefined } = props;
  const isMounted = useIsMounted();
  const { setValue, trigger } = useFormContext();
  const { errors } = useFormState();

  const [, setLoading] = useState<boolean>(true);
  const [fundraisers, setFundraisers] = useState<FundraiserResponse[]>();
  const [fundraiserError, setFundraiserError] = useState<string>();
  const [fundraiserId, setFundraiserId] = useState<number>();

  useEffect(() => {
    const errorMessage = getNestedFormErrors(errors, name);
    setFundraiserError(errorMessage);
  }, [errors, name]);

  useEffect(() => {
    const fetchData = async () => {
      const searchFilter: FundraiserSearchFilter = {
        filter: '',
        pageIndex: 0,
        pageSize: 500,
        sortCol: 'heading',
        sortDir: 'asc',
      };
      try {
        const result = await getAllFundraiser(searchFilter);
        if (isMounted()) {
          setFundraisers(result.data.results);
        }
      } catch (e) {
        if (isMounted()) {
          ConsoleHelper.log(e);
        }
      } finally {
        if (isMounted()) {
          setLoading(false);
        }
      }
    };

    fetchData();
  }, [isMounted]);

  const update = (val: number) => {
    setValue(name, val);
    setFundraiserId(val);
    if (callback) callback(val);
    trigger();
  };

  useEffect(() => {
    if (fundraisers?.length && initialValue) setFundraiserId(initialValue);
  }, [initialValue, fundraisers?.length]);

  return (
    <>
      <Box sx={{ display: 'none' }}>
        <ControlledTextField name={name} />
      </Box>
      <AutocompleteDropdown<FundraiserResponse>
        options={fundraisers || []}
        nameProp='heading'
        keyProp='fundraiser_id'
        name='fundraiserPicker'
        value={fundraisers ? fundraisers.filter((x) => x.fundraiser_id === fundraiserId)[0] : undefined}
        label={label}
        error={!!fundraiserError}
        callback={(val) => {
          update(val && val.fundraiser_id ? val.fundraiser_id : 0);
        }}
        componentWidth='15rem'
      />
      {fundraiserError && (
        <FormHelperText error sx={{ pl: 1 }}>
          <em>{fundraiserError}</em>
        </FormHelperText>
      )}
    </>
  );
};
